/* eslint-disable @typescript-eslint/no-explicit-any */
import { Tab } from "@headlessui/react";
import classNames from "classnames";
import { ReactNode } from "react";
import { useTranslation } from "next-i18next";

export type ActionTabHeaders = {
  id: number;
  title: string;
  isDisabled?: boolean;
};

export type TabStyle = "default" | "boxed" | "pills";

export type Props = {
  tabs: ActionTabHeaders[];
  tabSpacing?: "fill" | "hug";
  children?: React.ReactNode;
  onTabChange?: (selectedTabIndex: number) => void;
  selectedIndex?: number;
  className?: string;
  tabStyle?: TabStyle;
  extraContent?: ReactNode;
  colour?: "primary" | "secondary";
};

export const Tabs = ({
  tabs,
  className,
  tabSpacing = "hug",
  children,
  onTabChange,
  selectedIndex,
  tabStyle = "default",
  extraContent,
  colour = "primary",
}: Props) => {
  const { t } = useTranslation(["common"]);

  const getTabClassName = (selected: boolean, isDisabled?: boolean) => {
    let tabClassName = classNames(
      "text-md font-semibold whitespace-nowrap border-b-2 pb-1 snap-start px-4 md:px-0",
      {
        "border-b-2 border-primary-500 text-primary-500": selected,
        "border-transparent hover:text-gray-700 hover:border-gray-300":
          !selected,
        "w-full": tabSpacing === "fill",
        "text-gray-400": isDisabled,
      }
    );

    if (tabStyle === "boxed") {
      tabClassName = classNames(
        "border border-gray-100 px-4 py-2 text-sm font-medium cursor-pointer focus:outline-none hover:bg-gray-50",
        {
          "bg-primary-500 text-white hover:bg-primary-400": selected,
          "bg-white text-primary-500": !selected,
          "w-full": tabSpacing === "fill",
          "first:rounded-l-md first:border-r-0 [&:nth-last-child(2)]:border-l-0 [&:nth-last-child(2)]:rounded-r-md":
            tabs.length > 1 && extraContent,
          "first:rounded-l-md first:border-r-0 [&:nth-last-child(1)]:border-l-0 [&:nth-last-child(1)]:rounded-r-md":
            tabs.length > 1 && !extraContent,
          "rounded-md": tabs.length === 1,
        }
      );
    } else if (tabStyle === "pills") {
      tabClassName = classNames(
        "px-6 py-2 text-sm font-medium cursor-pointer rounded-md focus:outline-none",
        {
          "bg-primary-500 text-white hover:bg-primary-400":
            colour === "primary" && selected,
          "bg-white text-primary-500 hover:bg-gray-50":
            colour === "primary" && !selected,
          "bg-secondary-hover text-secondary-700 hover:bg-gray-200":
            colour === "secondary" && selected,
          "text-secondary-700 hover:bg-secondary-hover":
            colour === "secondary" && !selected,
        }
      );
    }

    return tabClassName;
  };

  return (
    <Tab.Group
      selectedIndex={selectedIndex}
      onChange={(selected: number) => {
        if (onTabChange) {
          onTabChange(selected);
        }
      }}
    >
      <Tab.List
        className={classNames("overflow-x-auto scroll-pl-6 snap-x", className, {
          "flex gap-5 space-x-1 py-4": className === undefined,
        })}
      >
        {Object(tabs).map((tab: ActionTabHeaders) => (
          <Tab
            disabled={tab.isDisabled}
            key={tab.id}
            className={({ selected }) =>
              getTabClassName(selected, tab.isDisabled)
            }
          >
            {t(tab.title as any)}
          </Tab>
        ))}
        {extraContent}
      </Tab.List>
      {children}
    </Tab.Group>
  );
};
